import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Layout } from "@/components/Layout";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, FileSpreadsheet, Users, Award, BookOpen, CalendarX, Save } from "lucide-react";
import { toast } from "sonner";

const GRADE_TONE = {
    "5_excellent": "bg-sage/15 text-sage border-sage/40",
    "4_very_good": "bg-seablue/15 text-seablue border-seablue/40",
    "3_good": "bg-amber-100 text-amber-800 border-amber-300",
    "2_sufficient": "bg-orange-100 text-orange-800 border-orange-300",
    "1_insufficient": "bg-terracotta/15 text-terracotta border-terracotta/40",
};

export default function KujdestariEndOfYearReport() {
    const [params, setParams] = useSearchParams();
    const [classes, setClasses] = useState([]);
    const [classId, setClassId] = useState(params.get("class_id") || "");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [genderEdits, setGenderEdits] = useState({});  // { student_id: gender }
    const [savingGender, setSavingGender] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get("/kujdestari/my-classes");
                setClasses(r.data);
                if (!classId && r.data.length > 0) setClassId(r.data[0].id);
            } catch { /* */ }
        })();
    }, []);

    const load = async () => {
        if (!classId) return;
        setLoading(true);
        setGenderEdits({});
        setParams({ class_id: classId });
        try {
            const r = await api.get("/kujdestari/end-of-year-report", { params: { class_id: classId } });
            setData(r.data);
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [classId]);

    const exportXlsx = async () => {
        try {
            const url = `${api.defaults.baseURL}/kujdestari/end-of-year-report.xlsx?class_id=${encodeURIComponent(classId)}`;
            const tk = localStorage.getItem("token");
            const resp = await fetch(url, { headers: { Authorization: `Bearer ${tk}` } });
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            const blob = await resp.blob();
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = `raport-fundvitit-${data?.class?.name?.replace(/\s+/g, "_") || "klasa"}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
        } catch (e) {
            toast.error("Eksporti dështoi");
        }
    };

    const printPDF = () => window.print();

    const setGender = (sid, g) => setGenderEdits((p) => ({ ...p, [sid]: g }));

    const saveGenders = async () => {
        const assignments = Object.entries(genderEdits).map(([student_id, gender]) => ({ student_id, gender }));
        if (assignments.length === 0) return;
        setSavingGender(true);
        try {
            await api.post("/kujdestari/students/gender", { class_id: classId, assignments });
            toast.success(`Gjinia u përditësua për ${assignments.length} nxënës`);
            setGenderEdits({});
            load();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
        finally { setSavingGender(false); }
    };

    if (classes.length === 0 && !loading) {
        return (
            <Layout>
                <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center max-w-2xl">
                    <Award className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                    <h1 className="font-heading text-2xl font-bold text-slate-800">Nuk jeni Kujdestar</h1>
                    <p className="text-sm text-slate-600 mt-2">Vetëm kujdestari i klasës mund të hartojë raportin e fundvitit.</p>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6 print:hidden">
                <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">Raporti i Fundvitit</div>
                    <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-black text-slate-900 tracking-tight">{data?.class?.name || "—"}</h1>
                    <p className="text-slate-600 mt-2 text-sm">Statistika notash sipas gjinisë • mungesa sipas muajve • mesatare lëndësh</p>
                </div>
                <div className="flex flex-wrap gap-2">
                    {classes.length > 1 && (
                        <select value={classId} onChange={(e) => setClassId(e.target.value)} className="h-10 rounded-md border border-slate-300 bg-white text-sm px-3 font-semibold" data-testid="report-class-select">
                            {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    )}
                    <Button variant="outline" onClick={printPDF} className="gap-2" data-testid="btn-print">
                        <Printer className="w-4 h-4" /> Printo / PDF
                    </Button>
                    <Button onClick={exportXlsx} className="gap-2 bg-sage hover:bg-sage/90" data-testid="btn-xlsx">
                        <FileSpreadsheet className="w-4 h-4" /> Eksporto Excel
                    </Button>
                </div>
            </div>

            {loading || !data ? (
                <div className="text-slate-500">Duke ngarkuar…</div>
            ) : (
                <div id="report" className="space-y-5">
                    {/* Print header (only visible on print) */}
                    <div className="hidden print:block mb-6 border-b-2 border-slate-900 pb-3">
                        <h1 className="text-2xl font-bold">German Gymnasium</h1>
                        <p className="text-sm text-slate-600">Raporti i Fundvitit — {data.class.name}</p>
                        <p className="text-xs text-slate-500">Gjeneruar: {data.generated_at?.slice(0, 10)}</p>
                    </div>

                    {/* Students count */}
                    <div className="bg-white border border-slate-200 rounded-2xl p-5">
                        <div className="flex items-center gap-2 mb-3">
                            <Users className="w-4 h-4 text-oxford" />
                            <h2 className="text-xs uppercase tracking-widest font-bold text-slate-700">Numri i nxënësve</h2>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <div className="bg-bone/40 border border-slate-200 rounded-xl p-4 text-center">
                                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Meshkuj</div>
                                <div className="font-heading text-3xl font-black text-oxford" data-testid="count-M">{data.students_count.M}</div>
                                <div className="text-[10px] text-slate-500 mt-1">{data.students_count.M_pct}%</div>
                            </div>
                            <div className="bg-bone/40 border border-slate-200 rounded-xl p-4 text-center">
                                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Femra</div>
                                <div className="font-heading text-3xl font-black text-terracotta" data-testid="count-F">{data.students_count.F}</div>
                                <div className="text-[10px] text-slate-500 mt-1">{data.students_count.F_pct}%</div>
                            </div>
                            <div className="bg-oxford/5 border border-oxford/30 rounded-xl p-4 text-center">
                                <div className="text-[10px] uppercase tracking-widest text-oxford font-bold">Total</div>
                                <div className="font-heading text-3xl font-black text-oxford" data-testid="count-total">{data.students_count.total}</div>
                                <div className="text-[10px] text-slate-500 mt-1">100%</div>
                            </div>
                        </div>

                        {/* Gender editor */}
                        <details className="mt-4 print:hidden">
                            <summary className="text-xs font-semibold text-slate-600 cursor-pointer hover:text-slate-900">Përditëso gjininë e nxënësve ({data.students.length})</summary>
                            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-80 overflow-y-auto">
                                {data.students.map((s) => {
                                    const cur = genderEdits[s.id] ?? s.gender;
                                    return (
                                        <div key={s.id} className="flex items-center gap-2 px-3 py-2 bg-bone/30 rounded-md text-sm" data-testid={`gender-${s.id}`}>
                                            <span className="flex-1 truncate">{s.name}</span>
                                            <select value={cur} onChange={(e) => setGender(s.id, e.target.value)} className="h-8 rounded-md border border-slate-300 px-2 text-xs">
                                                <option value="M">M</option>
                                                <option value="F">F</option>
                                                <option value="unknown">?</option>
                                            </select>
                                        </div>
                                    );
                                })}
                            </div>
                            {Object.keys(genderEdits).length > 0 && (
                                <Button onClick={saveGenders} disabled={savingGender} className="mt-3 gap-2 bg-oxford hover:bg-oxfordhover" size="sm" data-testid="save-gender">
                                    <Save className="w-4 h-4" /> Ruaj ({Object.keys(genderEdits).length})
                                </Button>
                            )}
                        </details>
                    </div>

                    {/* Grade distribution */}
                    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                        <div className="px-5 py-3 border-b border-slate-200 bg-bone/40 flex items-center gap-2">
                            <Award className="w-4 h-4 text-oxford" />
                            <h2 className="text-xs uppercase tracking-widest font-bold text-slate-700">Shpërndarja e notave (vit)</h2>
                            <div className="ml-auto text-[11px] text-slate-500">{data.grade_distribution_total} nota</div>
                        </div>
                        <table className="w-full text-sm">
                            <thead className="bg-bone/30">
                                <tr className="text-[10px] uppercase tracking-widest text-slate-600">
                                    <th className="text-left px-4 py-2 font-bold">Niveli</th>
                                    <th className="text-right px-4 py-2 font-bold">Meshkuj</th>
                                    <th className="text-right px-4 py-2 font-bold">Femra</th>
                                    <th className="text-right px-4 py-2 font-bold">Total</th>
                                    <th className="text-right px-4 py-2 font-bold">%</th>
                                </tr>
                            </thead>
                            <tbody>
                                {["5_excellent", "4_very_good", "3_good", "2_sufficient", "1_insufficient"].map((k) => {
                                    const b = data.grade_distribution[k];
                                    return (
                                        <tr key={k} className="border-t border-slate-100" data-testid={`bucket-${k}`}>
                                            <td className="px-4 py-2.5">
                                                <span className={`px-2 py-1 rounded-md border text-xs font-bold ${GRADE_TONE[k]}`}>{b.label}</span>
                                            </td>
                                            <td className="text-right px-4 py-2.5 font-mono">{b.M}</td>
                                            <td className="text-right px-4 py-2.5 font-mono">{b.F}</td>
                                            <td className="text-right px-4 py-2.5 font-mono font-bold">{b.total}</td>
                                            <td className="text-right px-4 py-2.5 font-mono">{b.pct}%</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Subject averages */}
                    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                        <div className="px-5 py-3 border-b border-slate-200 bg-bone/40 flex items-center gap-2">
                            <BookOpen className="w-4 h-4 text-sage" />
                            <h2 className="text-xs uppercase tracking-widest font-bold text-slate-700">Mesataret sipas lëndës</h2>
                            <div className="ml-auto text-[11px] text-slate-500">{data.subject_averages.length} lëndë</div>
                        </div>
                        {data.subject_averages.length === 0 ? (
                            <div className="p-8 text-center text-sm text-slate-500">Asnjë notë vjetore nuk është mbyllur ende</div>
                        ) : (
                            <table className="w-full text-sm">
                                <thead className="bg-bone/30">
                                    <tr className="text-[10px] uppercase tracking-widest text-slate-600">
                                        <th className="text-left px-4 py-2 font-bold">Lënda</th>
                                        <th className="text-right px-4 py-2 font-bold">Mesatare</th>
                                        <th className="text-right px-4 py-2 font-bold">M</th>
                                        <th className="text-right px-4 py-2 font-bold">F</th>
                                        <th className="text-right px-4 py-2 font-bold">Notash</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.subject_averages.map((s) => (
                                        <tr key={s.subject_id} className="border-t border-slate-100" data-testid={`subj-${s.subject_id}`}>
                                            <td className="px-4 py-2.5 font-semibold text-slate-800">{s.subject_name}</td>
                                            <td className="text-right px-4 py-2.5 font-mono font-bold text-oxford">{s.avg}</td>
                                            <td className="text-right px-4 py-2.5 font-mono text-slate-600">{s.M_avg ?? "—"}</td>
                                            <td className="text-right px-4 py-2.5 font-mono text-slate-600">{s.F_avg ?? "—"}</td>
                                            <td className="text-right px-4 py-2.5 font-mono text-slate-500">{s.count}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {/* Absence matrix */}
                    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                        <div className="px-5 py-3 border-b border-slate-200 bg-bone/40 flex items-center gap-2">
                            <CalendarX className="w-4 h-4 text-terracotta" />
                            <h2 className="text-xs uppercase tracking-widest font-bold text-slate-700">Matrica e mungesave (orë)</h2>
                            <div className="ml-auto text-[11px] text-slate-500">Total vit: <strong>{data.absence_totals.year.total}</strong></div>
                        </div>
                        <table className="w-full text-sm">
                            <thead className="bg-bone/30">
                                <tr className="text-[10px] uppercase tracking-widest text-slate-600">
                                    <th className="text-left px-4 py-2 font-bold">Sem</th>
                                    <th className="text-left px-4 py-2 font-bold">Muaji</th>
                                    <th className="text-right px-4 py-2 font-bold text-sage">Të arsyeshme</th>
                                    <th className="text-right px-4 py-2 font-bold text-terracotta">Të paarsyeshme</th>
                                    <th className="text-right px-4 py-2 font-bold">Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.absence_matrix.map((r) => (
                                    <tr key={`${r.semester}-${r.month}`} className="border-t border-slate-100" data-testid={`mtx-${r.month}`}>
                                        <td className="px-4 py-2 text-slate-500 text-[11px]">Sem {r.semester}</td>
                                        <td className="px-4 py-2 font-semibold">{r.label}</td>
                                        <td className="text-right px-4 py-2 font-mono text-sage">{r.excused}</td>
                                        <td className="text-right px-4 py-2 font-mono text-terracotta">{r.unexcused}</td>
                                        <td className="text-right px-4 py-2 font-mono font-bold">{r.total}</td>
                                    </tr>
                                ))}
                                <tr className="border-t-2 border-slate-300 bg-bone/30">
                                    <td className="px-4 py-2 font-bold" colSpan={2}>Totali Sem 1</td>
                                    <td className="text-right px-4 py-2 font-mono font-bold text-sage">{data.absence_totals.semester_1.excused}</td>
                                    <td className="text-right px-4 py-2 font-mono font-bold text-terracotta">{data.absence_totals.semester_1.unexcused}</td>
                                    <td className="text-right px-4 py-2 font-mono font-bold">{data.absence_totals.semester_1.total}</td>
                                </tr>
                                <tr className="bg-bone/30">
                                    <td className="px-4 py-2 font-bold" colSpan={2}>Totali Sem 2</td>
                                    <td className="text-right px-4 py-2 font-mono font-bold text-sage">{data.absence_totals.semester_2.excused}</td>
                                    <td className="text-right px-4 py-2 font-mono font-bold text-terracotta">{data.absence_totals.semester_2.unexcused}</td>
                                    <td className="text-right px-4 py-2 font-mono font-bold">{data.absence_totals.semester_2.total}</td>
                                </tr>
                                <tr className="border-t-2 border-slate-900 bg-oxford text-white">
                                    <td className="px-4 py-2.5 font-bold" colSpan={2}>TOTALI VJETOR</td>
                                    <td className="text-right px-4 py-2.5 font-mono font-bold">{data.absence_totals.year.excused}</td>
                                    <td className="text-right px-4 py-2.5 font-mono font-bold">{data.absence_totals.year.unexcused}</td>
                                    <td className="text-right px-4 py-2.5 font-mono font-bold">{data.absence_totals.year.total}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    {/* Print signatures */}
                    <div className="hidden print:grid grid-cols-2 gap-12 mt-12">
                        <div>
                            <div className="border-b border-slate-900 mb-2 h-12"></div>
                            <p className="text-xs text-slate-700">Kujdestari i klasës</p>
                        </div>
                        <div>
                            <div className="border-b border-slate-900 mb-2 h-12"></div>
                            <p className="text-xs text-slate-700">Drejtori</p>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                @media print {
                    @page { size: A4; margin: 18mm; }
                    body { background: white !important; }
                    header, footer, .print\\:hidden { display: none !important; }
                    main { padding: 0 !important; max-width: 100% !important; }
                    .bg-bone\\/40, .bg-bone\\/30 { background: #f5f1eb !important; }
                    table { page-break-inside: auto; }
                    tr { page-break-inside: avoid; page-break-after: auto; }
                }
            `}</style>
        </Layout>
    );
}
